import { useEffect, useState } from "react";
import { Checklist } from "@/bryntum-lib/models/Checklist";
import { Button } from "@/modern/components/ui/button";
import { Input } from "@/modern/components/ui/input";
import { AnimatePresence, motion } from "motion/react";
import { useSwivuChecklistItems } from "@/react/hooks/entityHooks/useSwivuChecklistItems";
import { ChecklistRowComponent } from "@/react/components/entityViews/checklist/components/ChecklistRow";
import { AnimatedChecklistItemRow } from "@/react/components/entityViews/checklist/components/ChecklistItemRow";
import { ArrowLeftIcon, CheckCircle2, ListTodo, ListTodoIcon, Plus } from "lucide-react";
import { cn } from "@/modern/utils";
import { useChecklistItemsData } from "@/react/hooks/entityHooks/useChecklistItemsData";

interface ChecklistDetailViewProps {
  checklist: Checklist;
  onBack: () => void;
  handleListView: () => void;
}

export const ChecklistDetailView = ({ checklist, onBack, handleListView }: ChecklistDetailViewProps) => {
  const [checklistItemTitle, setChecklistItemTitle] = useState("");
  const { createChecklistItem } = useSwivuChecklistItems();
  const { items: checklistItems } = useChecklistItemsData(checklist.id as number);
  const [showAddItem, setShowAddItem] = useState(checklistItems.length === 0);

  useEffect(() => {
    if (checklistItems.length === 0 && !showAddItem) {
      setShowAddItem(true);
    }
  }, [checklistItems.length, showAddItem]);
  // Auto-show modal when items are empty - derived logic without useEffect

  const handleCreateChecklistItem = () => {
    if (checklistItemTitle.trim()) {
      createChecklistItem(checklist, checklistItemTitle);
      setChecklistItemTitle("");
    }
  };

  return (
    <div className="flex flex-col h-full relative min-w-0 flex-1 min-h-0">
      {/* Sticky Header - Using same component as list */}
      <div className="flex-none  pb-3 border-b flex gap-2 min-w-0 ">
        <div className="flex gap-2 animate-in fade-in slide-in-from-bottom-2 duration-700 flex-shrink-0 ">
          <Button variant="ghost" size="icon-lg" onClick={onBack} className="">
            <ArrowLeftIcon />
          </Button>
        </div>

        <motion.div layout="position" layoutId={`checklist-${checklist.id}`} className="flex-1 flex min-w-0 pr-3 ">
          <ChecklistRowComponent handleListView={handleListView} checklist={checklist} showAsHeader={true} />
        </motion.div>
      </div>

      {/* Add Item Section */}
      <AnimatePresence>
        {showAddItem && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ bounce: 0, type: "spring" }}>
            <div className="relative p-4">
              <div className="absolute -inset-1  rounded-xl blur-sm opacity-75"></div>
              <div className="relative bg-card border border-foreground/20 rounded-xl p-4 shadow hover:border-foreground/40 transition-all duration-300">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center size-8 rounded-lg bg-foreground/10 text-foreground">
                    <ListTodoIcon className="size-4" />
                  </div>
                  <h3 className="font-semibold text-sm">Add New Item</h3>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="What needs to be done?"
                    value={checklistItemTitle}
                    onChange={(e) => setChecklistItemTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && checklistItemTitle.trim()) {
                        handleCreateChecklistItem();
                      }
                    }}
                    className={cn("flex-1 border-2 transition-all duration-200", checklistItemTitle.trim() ? "border-foreground/50 focus:border-foreground" : "border-border")}
                  />
                  <Button
                    disabled={!checklistItemTitle.trim()}
                    variant="default"
                    size="icon"
                    onClick={handleCreateChecklistItem}
                    className="size-10 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
                  >
                    <Plus className="size-5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={checklistItems.length === 0}
                    onClick={() => setShowAddItem(false)}
                    className="size-10 hover:bg-success/90 bg-success shadow-md hover:shadow-lg text-success-foreground hover:text-success-foreground/90"
                  >
                    <CheckCircle2 className="size-5" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scrollable Content */}
      <div
        // className="flex-1 overflow-y-auto"
        className="flex-1 overflow-y-auto space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500"
      >
        {/* Checklist Items */}
        <div className="py-4">
          {checklistItems.length === 0 && (
            <div key="no-items" className="flex flex-col items-center justify-center px-4">
              <div className="inline-flex items-center justify-center size-16 rounded-full bg-muted/50 mb-4">
                <ListTodo className="size-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-sm">No items yet. Add your first item above to get started!</p>
            </div>
          )}
          <AnimatePresence mode="popLayout" initial={false}>
            {checklistItems
              .sort((a, b) => a.order - b.order)
              .filter((item) => !!item.description)
              .map((item) => {
                if (!item.description) return null;
                return <AnimatedChecklistItemRow key={item.stableKey} item={item} className="px-4" />;
              })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
